/**
 * <AuthProvider> + AuthStore — Wave 3 React surface.
 *
 * The Store is an `Object.is`-gated external store (useSyncExternalStore-shaped)
 * holding the current AuthSnapshot. One instance per app — Plan 05's
 * `createAppRoot` instantiates it once and passes the singleton through the
 * React tree. The Provider wraps the app in a Context (so useAuth can resolve
 * the store without prop-drilling) and owns two lifecycle concerns:
 *
 *   1. `installBfcacheHandler()` — visibilitychange listener that pauses the
 *      proactive-refresh timer while hidden and reschedules on resume (Pitfall
 *      5 from RESEARCH). Install on mount; no teardown needed — it's a global
 *      singleton and idempotent.
 *   2. `setupAuthChannelHandlers({...})` — cross-tab BC subscriber (Plan 04).
 *      TOKEN_REFRESHED primes the local store; LOGOUT / FAMILY_REVOKED flush
 *      the store and hard-redirect to `/login`. Subscribe on mount, unsubscribe
 *      on unmount.
 *
 * The provider is intentionally thin — all heavy lifting (refresh, single-flight,
 * token storage) lives in Plan 02/04's pure modules. This file is the React
 * glue + cleanup boundary.
 */
import { createContext, useContext, useEffect, type ReactNode, type JSX } from "react";
import type { AuthSnapshot, Audience } from "../types.js";
import { UNAUTH_SNAPSHOT } from "../types.js";
import { setupAuthChannelHandlers } from "../refresh/crossTabCoordinator.js";
import { installBfcacheHandler } from "../refresh/proactiveTimer.js";
import { safeRedirectPath } from "../guards/redirectGuard.js";
import { currentRouterPath } from "../guards/currentRouterPath.js";

/**
 * External-store shape (matches `useSyncExternalStore`).
 *
 * `Object.is` gate in `set()` skips pointless re-renders when consumers
 * reassign to the same snapshot object (e.g. refreshOnLoad returning the
 * shared UNAUTH_SNAPSHOT constant on an already-unauth app).
 */
export class AuthStore {
  private snapshot: AuthSnapshot = UNAUTH_SNAPSHOT;
  private listeners = new Set<() => void>();

  getSnapshot = (): AuthSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  set(next: AuthSnapshot): void {
    if (Object.is(this.snapshot, next)) return;
    this.snapshot = next;
    this.listeners.forEach((l) => {
      l();
    });
  }
}

/** Module-local singleton. `createAppRoot` is the single legal writer. */
export const authStore = new AuthStore();

const AuthStoreContext = createContext<AuthStore>(authStore);

// App-level audience (admin | mobile | platform). Published by
// `createAppRoot` so action creators (useAuth().login) can inject it into
// login request bodies without prop-drilling through LoginForm.
const AuthAudienceContext = createContext<Audience>("admin");

/** Internal — `useAuth()` reads this. Exported only for rare advanced escape hatches. */
export function useAuthStore(): AuthStore {
  return useContext(AuthStoreContext);
}

/** App-level audience published by `createAppRoot`. Read inside useAuth to
 *  inject `audience` into tenant-login request bodies (backend contract). */
export function useAuthAudience(): Audience {
  return useContext(AuthAudienceContext);
}

export interface AuthProviderProps {
  store: AuthStore;
  children: ReactNode;
  /** Audience for this app — "admin" / "mobile" / "platform". Matches
   *  `createAppRoot({ aud })`. Exposed via useAuthAudience() for the login
   *  action creator. */
  audience: Audience;
  /**
   * Route to navigate to when the BC receives LOGOUT or FAMILY_REVOKED. Apps
   * usually leave this at the default.
   * @default `${import.meta.env.BASE_URL}login`
   */
  loginPath?: string;
}

export function AuthProvider({
  store,
  children,
  audience,
  loginPath = `${(import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? "/"}login`,
}: AuthProviderProps): JSX.Element {
  useEffect(() => {
    // bfcache handler is idempotent — safe to call repeatedly; installs a
    // single global visibilitychange listener.
    installBfcacheHandler();

    const unsubscribe = setupAuthChannelHandlers({
      onTokenRefreshed: (msg) => {
        // Plan 04's coordinator already wrote the token to accessTokenStore
        // before invoking this callback. We only need to mirror the new
        // access token on the snapshot so React consumers re-render with a
        // fresh value (claims are unchanged by a refresh — same user / tenant
        // / roles / aud).
        //
        // Plan 08.1-02: TOKEN_REFRESHED carries fresh effectivePermissions
        // when the leader fetched them. `undefined` means "leader hit the
        // short-circuit" — keep the existing perms (avoids transient
        // gate-deny flash mid-rotation per RESEARCH §Pitfall 2).
        const cur = store.getSnapshot();
        const next: typeof cur = { ...cur, accessToken: msg.accessToken };
        if (msg.effectivePermissions !== undefined) {
          next.effectivePermissions = new Set(msg.effectivePermissions);
        }
        if (msg.mustChangePassword !== undefined) {
          next.mustChangePassword = msg.mustChangePassword;
        }
        store.set(next);
        // Another tab finished the password change: this tab's change form is
        // dead (its temporary password no longer works). Reload into the app.
        if (
          cur.mustChangePassword === true &&
          msg.mustChangePassword === false &&
          currentRouterPath().split("?")[0] === "/auth/change-password"
        ) {
          window.location.assign(
            (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? "/",
          );
        }
      },
      onLogout: () => {
        store.set(UNAUTH_SNAPSHOT);
        window.location.assign(loginPath);
      },
      onFamilyRevoked: () => {
        store.set(UNAUTH_SNAPSHOT);
        // Preserve deep-link through login round-trip, open-redirect-safe.
        const currentPath = safeRedirectPath(currentRouterPath());
        const redirect = encodeURIComponent(currentPath);
        window.location.assign(`${loginPath}?reason=session_revoked&redirect=${redirect}`);
      },
    });

    return unsubscribe;
  }, [store, loginPath]);

  return (
    <AuthStoreContext.Provider value={store}>
      <AuthAudienceContext.Provider value={audience}>{children}</AuthAudienceContext.Provider>
    </AuthStoreContext.Provider>
  );
}
